// setup methods for external use of Axios
import axios from 'axios';

import { cookieExists } from 'Utils/cookies';

// axios global
export const baseURL = import.meta.env.VITE_APP_API_URL;
export const headers = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

// axios api instance
const instance = axios.create({
  baseURL,
  headers,
  withCredentials: true,
  withXSRFToken: true,
});

// making the api request
const request = async (url: string, requestData: object = {}, requestType = 'get', options = {}) =>
  await instance[requestType](url, requestData, options);

// TODO: remove after the timecard export csv issue is fixed in the backend
export const csvHeaders = {
  'Content-Type': 'application/json',
  Accept: 'text/csv;charset=UTF-8',
};

const csvInstance = axios.create({
  baseURL,
  headers: csvHeaders,
  withCredentials: true,
});

const csvRequest = async (url: string, requestData: object = {}, requestType = 'get', options = {}) =>
  await csvInstance[requestType](url, requestData, options);

const csvGet = async (url: string, params = {}) => await csvRequest(url, params);

// Perform a get and return the data
const get = async (url: string, params = {}) => await request(url, params);
const post = async (url: string, data: object, options?: object) => await request(url, data, 'post', options);
const put = async (url: string, data: object, options?: object) => await request(url, data, 'put', options);

const patch = async (url: string, data: object, options?: object) => await request(url, data, 'patch', options);

// delete is a key word and cannot be used in strict mode. Hence deleteFn
const deleteFn = async (url: string, data = {}, options = {}) => await request(url, data, 'delete', options);
// Set the Authorization token
const setAuthorizationToken = (token: string = '') => {
  instance.defaults.headers.Authorization = `Bearer ${token}`;
  localStorage.setItem('auth_token', token);
};

// Remove the Authorization token
const resetAuthorizationToken = () => {
  // enable this to have a new token on each login
  delete instance?.defaults?.headers?.Authorization;
  localStorage.removeItem('auth_token');
};

// Restore token from localStorage on app init
const storedToken = localStorage.getItem('auth_token');
if (storedToken) {
  instance.defaults.headers.Authorization = `Bearer ${storedToken}`;
}

// Get the initial headers and cookies setup for csrf
const csrfHeader = async () => {
  await request(import.meta.env.VITE_APP_SANCTUM_URL);
  // Check for the cookie XSRF-TOKEN
  return cookieExists('XSRF-TOKEN');
};

// Auto-append locationHint on GHL embed API calls for multi-location resolution
const GHL_SELECTED_LOCATION_KEY = 'ghl_selected_location';
instance.interceptors.request.use((config) => {
  if (config.headers?.['X-GHL-SSO']) {
    try {
      const hint = localStorage.getItem(GHL_SELECTED_LOCATION_KEY);
      if (hint) {
        config.params = { ...config.params, locationHint: hint };
      }
    } catch { /* ignore storage errors */ }
  }
  return config;
});

// Auto-retry once on 419 (CSRF token mismatch) by refreshing the token
const sanctumUrl = import.meta.env.VITE_APP_SANCTUM_URL;
instance.interceptors.response.use(undefined, async (error) => {
  const original = error.config;
  if (error.response?.status === 419 && !original._retried && original.url !== sanctumUrl) {
    original._retried = true;
    await csrfHeader();
    return instance.request(original);
  }
  return Promise.reject(error);
});

export const Api = {
  get,
  post,
  put,
  patch,
  delete: deleteFn,
  setAuthorizationToken,
  resetAuthorizationToken,
  csrfHeader,
  csvGet,
};
